
import { Component as VC } from 'vue/types/umd';
import {
  Component,
  Prop,
  Vue,
  Watch,
  Provide,
  Getter,
} from 'nuxt-property-decorator';
import {
  ISanityDocuments,
  SanityDocumentSchema,
  ProjectPageVariation,
} from '~/types/sanity-documents';
import ProjectAboutTab from './ProjectAboutTab.vue';
import ProjectFaqsTab from './ProjectFaqsTab.vue';
import ProjectSaleTab from './ProjectSaleTab.vue';
import ProjectUpdatesTab from './ProjectUpdatesTab.vue';
import ProjectEpisodesTab from './ProjectEpisodesTab.vue';
import ProjectExpansionsTab from './ProjectExpansionsTab.vue';
import { IProject } from '~/types/project';
import { IMysteryBox } from '~/types/collections';

export interface ITabGroup {
  badge?: string;
  component: VC;
  key: string;
  props: Record<string, any>;
  showDot?: boolean;
  title: string;
}

export enum Tabs {
  ABOUT = 'about',
  WATCH = 'watch',
  EXPANSIONS = 'expansions',
  FAQS = 'faqs',
  OWN = 'own',
  STORE = 'store',
  UPDATES = 'updates',
}

@Component({
  components: {
    ProjectSaleTab,
  },
})
export default class ProjectTabs extends Vue {
  @Prop(Boolean) readonly isSaleLive!: boolean;
  @Prop(Boolean) readonly loading!: boolean;
  @Prop(Object) readonly doc!: ISanityDocuments[SanityDocumentSchema.PROJECT];
  @Prop({ type: String, default: '' }) readonly tab!: string;
  @Prop(Object) readonly project!: IProject;

  @Provide() changeTab = this.clickEventToChangeTab.bind(this);

  @Getter('getMysteryBoxesByProjectId', { namespace: 'collections' })
  getMysteryBoxesByProjectId!: (projectSlug: string) => IMysteryBox[];

  tabsHeight = 72;
  activeTab = '';
  variations = ProjectPageVariation;

  clickEventToChangeTab(tab: string) {
    this.activeTab = tab;
    this.scrollToTabs();
  }

  @Watch('tab')
  handleTabInputChange(
    current: string | undefined,
    previous: string | undefined,
  ) {
    if (current !== undefined && current !== previous) {
      this.activeTab = current;
      this.scrollToTabs();
    }
  }

  @Watch('tabs')
  checkTabChange(
    current: { key: string }[] | undefined,
    previous: { key: string }[] | undefined,
  ) {
    const currentSnap =
      current
        ?.map?.((tab) => tab.key)
        ?.sort?.()
        ?.join?.(',') || [];
    const prevSnap =
      previous
        ?.map?.((tab) => tab.key)
        ?.sort?.()
        ?.join?.(',') || [];
    if (current !== undefined && currentSnap !== prevSnap) {
      this.$emit('tabRender');
    }
  }

  handleTabsChange() {
    if (!this.activeTab) {
      return;
    }
    this.scrollToTabs();
    const tabToEmit =
      this.activeTab === this.tabs[0]?.key ? undefined : this.activeTab;
    this.$emit('update:tab', tabToEmit);
    this.$router.replace({
      path: this.$route.path,
      hash: this.$route.hash,
      query: {
        ...this.$route.query,
        tab: tabToEmit,
      },
    });
  }

  scrollToTabs() {
    if (this.$refs.tabs) {
      const tabs = this.$refs.tabs as HTMLElement;
      const elPos = tabs.getBoundingClientRect();
      if (elPos.y < 0) {
        (this.$refs.tabs as HTMLElement)?.scrollIntoView({
          behavior: 'smooth',
        });
      }
    }
  }

  created() {
    this.activeTab = this.tab;
  }

  get hasMysteryBoxes() {
    return (
      this.getMysteryBoxesByProjectId(this.project.slug).length > 0 || false
    );
  }

  get variation() {
    return this.$sanityLookup.getSiteConfig()?.data?.projectPageVariation;
  }

  get tabs() {
    const tabs: ITabGroup[] = [];

    if (this.project?.series) {
      tabs.push({
        key: Tabs.WATCH,
        title: this.$t('components.films.filmInfoTabs.tabs.episodes') as string,
        props: {
          episodes: this.project.series.episodes,
          projectSlug: this.project.slug,
          project: this.project,
        },
        component: ProjectEpisodesTab,
      });
    }

    // Expansions Tab
    if (this.project?.posters && this.project.posters.length) {
      tabs.push({
        key: Tabs.EXPANSIONS,
        title: this.$t(
          'components.films.filmInfoTabs.tabs.expansions',
        ) as string,
        showDot: this.hasMysteryBoxes,
        props: {
          projectSlug: this.project.slug,
          project: this.project,
          doc: this.doc,
        },
        component: ProjectExpansionsTab,
      });
    }

    if (this.variation === this.variations.V2 && this.isSaleLive) {
      tabs.push({
        key: Tabs.STORE,
        title: this.$t('components.films.filmInfoTabs.tabs.store') as string,
        component: ProjectSaleTab,
        props: {
          loading: this.loading,
          doc: this.doc,
          isSaleLive: this.isSaleLive,
          variation: this.variation,
        },
      });
    }

    tabs.push({
      key: Tabs.ABOUT,
      title: this.$t('components.films.filmInfoTabs.tabs.about') as string,
      props: {
        loading: this.loading,
        doc: this.doc,
      },
      component: ProjectAboutTab,
    });

    if (
      this.variation === this.variations.V1 &&
      this.$vuetify.breakpoint.mdAndDown
    ) {
      tabs.push({
        key: Tabs.OWN,
        title: this.$t('components.films.filmInfoTabs.tabs.own') as string,
        component: ProjectSaleTab,
        props: {
          loading: this.loading,
          doc: this.doc,
          isSaleLive: this.isSaleLive,
          variation: this.variation,
        },
      });
    }

    if (this.doc?.updates?.length) {
      tabs.push({
        key: Tabs.UPDATES,
        badge:
          this.$options?.filters?.formatNumber?.(
            this.doc.updates.length,
            '0,a',
          ) || `${this.doc.updates.length}`,
        title: this.$t('components.films.filmInfoTabs.tabs.updates') as string,
        component: ProjectUpdatesTab,
        props: {
          loading: this.loading,
          doc: this.doc,
        },
      });
    }

    if (this.doc?.faqs?._ref) {
      tabs.push({
        key: Tabs.FAQS,
        title: this.$t('components.films.filmInfoTabs.tabs.faqs') as string,
        component: ProjectFaqsTab,
        props: {
          id: this.doc.faqs._ref,
        },
      });
    }

    return tabs;
  }
}
